import { useIsFetching } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useRestaurantBasePolicies } from '../../api/getRestaurantBasePolicies';
import { useRestaurantTaxPolicies } from '../../api/getRestaurantTaxPolicies';
import { CheckoutGuardOutletContext } from '../../routes/CheckoutGuard';
import { Deposits } from '../__design-system/modal/checkout/deposits';
import { HowDoBookingFeesWork } from '../__design-system/modal/info/booking-fees-info';
import { CouponIcon, GuestCountIcon } from '../icons';
import Spinner from '../Spinner';
import { InvoiceItem } from './InvoiceItem';
import {
	doesItemHavePerGuestCharges,
	getDepositLineItems,
	getPromoCodeDiscountItem,
	getTableSelectionFeeItem,
	getTaxBreakdown,
	sumDeferredLineItems,
	sumDepositLineItems,
	sumOtherLineItems
} from './util';

export const OrderSummary = () => {
	const { checkoutSession: session, invoice, restaurant } = useOutletContext<CheckoutGuardOutletContext>();
	const { t } = useTranslation();
	const { lineItems = [], deposit = 0, subtotalPriceAdjustments = [] } = invoice || {};
	const isSessionReloading = useIsFetching({
		queryKey: ['checkoutSession']
	});

	const { data: taxPolicies = [] } = useRestaurantTaxPolicies(restaurant);
	const { data: basePolicies = [] } = useRestaurantBasePolicies(restaurant?.id);
	const tableFeeItem = getTableSelectionFeeItem(lineItems);
	const promoCodeItem = getPromoCodeDiscountItem(lineItems);

	const DEFAULT_DEPOSIT_LABEL = t('titles.deposit');
	const depositLineItems = getDepositLineItems(lineItems, DEFAULT_DEPOSIT_LABEL, session.guests, basePolicies);
	const depositAmount = Math.max(sumDepositLineItems(lineItems), deposit);
	const deferredPayment = sumDeferredLineItems(lineItems);
	const otherAmount = sumOtherLineItems(subtotalPriceAdjustments);
	const taxBreakdown = getTaxBreakdown(invoice, taxPolicies);

	const displayPrice = tableFeeItem.total;

	return (
		<>
			{!!isSessionReloading ? (
				<ul className='OrderSummary'>
					<Spinner size='xs' />
				</ul>
			) : null}
			{!isSessionReloading && (
				<ul className='OrderSummary'>
					{/* Table Selection Fee, and Promo Code Discount (if any) */}
					<InvoiceItem
						key={tableFeeItem.item.id}
						title={t('titles.table_selection_fee')}
						subtitle={doesItemHavePerGuestCharges(tableFeeItem) && <GuestCountIcon guests={session.guests} />}
						amount={Math.max(displayPrice, 0)}
						currency={restaurant.currency}
						iconAfter={<HowDoBookingFeesWork />}
					/>
					{!!promoCodeItem && (
						<InvoiceItem
							key={promoCodeItem?.item.id}
							iconBefore={<CouponIcon />}
							title={promoCodeItem?.item.id}
							amount={Math.abs(Math.min(promoCodeItem?.total, 0))}
							currency={restaurant.currency}
							isNegative
						/>
					)}

					{/* Partnership Discount */}
					{!!(invoice?.discountAmount) && (
						<>
							<InvoiceItem
								title={`${invoice?.discountOrigin || 'Partner'} Exclusive Discount`}
								amount={-(invoice?.discountAmount || 0)}
								currency={restaurant.currency}
							/>
						</>
					)}

					{/* Subtotal */}
					<InvoiceItem title={t('titles.subtotal')} amount={invoice?.subtotal || 0} currency={restaurant.currency} />

					{/* Individual Taxes */}
					{taxBreakdown.filter(tax => tax.amount > 0).map((tax, index) =>
						(
							<InvoiceItem
								key={tax.taxId || index}
								title={`${tax.name} (${tax.percentage.toFixed(tax.percentage % 1 === 0 ? 0 : 3)}%)`}
								amount={tax.amount}
								currency={restaurant.currency}
								isDivided={false}
							/>
						)
					)}

					{/* Other */}
					{otherAmount === 0 ? null : <InvoiceItem title={t('titles.other')} amount={otherAmount} currency={restaurant.currency} />}

					{/* Deposit */}
					{depositLineItems.length > 0 ? (
						depositLineItems.map((depositItem, index) => (
							<InvoiceItem
								key={index}
								title={depositItem.label}
								iconAfter={depositItem.label === DEFAULT_DEPOSIT_LABEL ? <Deposits /> : undefined}
								amount={depositItem.amount}
								currency={restaurant.currency}
								isDivided={index === depositLineItems.length - 1}
							/>
						))
					) : depositAmount > 0 ? (
						<InvoiceItem
							title={DEFAULT_DEPOSIT_LABEL}
							iconAfter={<Deposits />}
							amount={depositAmount}
							currency={restaurant.currency}
						/>
					) : null}

					{/* Total */}
					<InvoiceItem title={t('titles.due_today')} amount={invoice?.total || 0} currency={restaurant.currency} isDivided={false} variant='bold' />
					{/* <InvoiceItem title='Total' amount={checkoutInvoice.total} isDivided={!!prevInvoice}/> */}

					{/* Deferred Payment */}
					{deferredPayment > 0 && (
						<InvoiceItem title={t('titles.deferred_payment')} amount={deferredPayment} currency={restaurant.currency} />
					)}

					{/* Previous invoices (for modifications) */}
					{/* ===================================== */}

					{/* {!prevInvoice || prevInvoice.total === 0 || !showingChanges ? null : (
						<InvoiceItem title='Existing Payment' amount={prevInvoice.total} />
					)} */}

					{/* {!prevInvoice ? null : showingChanges ? (
						modificationType !== 'cancel' && (
							<InvoiceItem
								title={payable < 0 ? 'Refund' : 'Amount Due'}
								amount={Math.abs(payable)}
								isBold
							/>
						)
					) : (
						<InvoiceItem title='Amount Paid' amount={total} />
					)} */}
				</ul>
			)}
		</>
	);
};
